import React from "react";
import ReactDOM from "react-dom";
import { HashRouter, Route, Routes } from "react-router-dom";
import cloneDeep from "lodash/cloneDeep";
import { MdArrowBack, MdArrowForward } from "react-icons/md";
import { SHA256 } from "crypto-js";

import {
    AAPMediaToolkitCurrentUserChild,
    AAPMediaToolkitCurrentUserInfo,
    AAPMediaToolkitPageData,
    ELCID,
    IAAPMediaToolkitVerbiageData,
    IAAPServerPlan,
    IMediaPlanPage,
    IMediaToolkitDataTopic,
    IMediaToolkitNavPage,
    IMediaToolkitProps,
    IMediaToolkitState,
    IPromisePoint,
    TBreakpoint,
} from "./models/IMediaToolkit";
import "./MediaToolkit.css";

import { LandingPage, FamilyConfigPage, TopicPage, FinishPage, PrioritiesPage, TutorialPage } from "./pages";
import IFamily, { EAge, ICustomPoint, IFamilyMember } from "./models/IFamily";
import { avatars } from "./components/Avatar";
import { CtaMap } from "./components/CtaMap";
import { INavButtonProps, NavButton } from "./components/NavButton";
import AAPService from "./services/AAPService";
import { ITranslations, strings } from "./data/strings";
import { DropdownOptions } from "./components/DropdownOptions";

const LOCALSTORAGEKEY = "AAP.MediaToolkit.MyPlanInfo";
const LOCALSTORAGEKEY_GUEST = "AAP.MediaToolkit.GuestPlanInfo";
const LOCALSTORAGEKEY_GUESTID = "AAP.MediaToolkit.GuestPlanID";
const TOOLKITLOCALSTORAGEKEY = `AAP.MediaToolkit.ToolInfo.${window.location.href.toLowerCase().indexOf('spanish') > -1 ? `Spanish` : `English`}`;
const NAVBUTTONTOPOFFSET = 250;

export const getRandomAvatar = (): number => {
    let randomNumber = Math.round(Math.random() * (avatars.length - 1));

    if (randomNumber > avatars.length - 1) {
        randomNumber = avatars.length - 1;
    }

    if (randomNumber < 0) {
        randomNumber = 0;
    }

    return randomNumber;
};

export const defaultFamilyMember: IFamilyMember = {
    name: "",
    age: null,
    avatar: getRandomAvatar(),
    pointsSelected: [],
    pointsRemoved: [],
};

export const defaultFamily: IFamily = {
    name: "",
    members: [],
    customPoints: [],
    priorities: [],
};

export const breakpointWidthMap: { [breakpoint in TBreakpoint]: { min: number; max: number } } = {
    xs: { min: 0, max: 639 },
    sm: { min: 640, max: 767 },
    md: { min: 768, max: 1023 },
    lg: { min: 1024, max: 1279 },
    xl: { min: 1280, max: 1535 },
    "2xl": { min: 1536, max: 99999 },
};

export const generateNewFamily = (): IFamily => {
    const tempFamily = cloneDeep(defaultFamily);

    tempFamily.members.push(cloneDeep(defaultFamilyMember));

    return tempFamily;
};

export const generateNewPlan = (): IAAPServerPlan => {
    const newPlan: IAAPServerPlan = {
        family: generateNewFamily(),
        json_encrypted: "",
        plan_guid: generateRandomHash(),
        is_public: false,
        hide_child_names: false,
        hide_last_name: false,
        plan_password_hash: "",
        id: null,
    };

    return newPlan;
};

export const generateRandomHash = (): string => {
    var now = new Date();
    return SHA256(now.getTime().toString()).toString();
};

export const parseQueryString = (): { [key: string]: string } => {
    const queryStrKeyValue: { [key: string]: string } = {};

    const urlArr = window.location.href.split("?");
    const queryStr = urlArr.length === 2 ? window.location.href.split("?").pop()?.split("#")[0] : null;

    if (!queryStr) {
        // return empty if no querystring
        return {};
    }

    queryStr.split("&").forEach((queryStrPair) => {
        queryStrKeyValue[queryStrPair.split("=")[0]] = decodeURIComponent(queryStrPair.split("=").pop() || "");
    });

    return queryStrKeyValue;
};

export default class MediaToolkit extends React.Component<IMediaToolkitProps, IMediaToolkitState> {
    protected ENCRYPTIONPW = "AAP.MediaToolkit.V2";
    public state: IMediaToolkitState = {
        loading: true,
        initiallyLoaded: false,
        saving: false,
        isLoggedIn: false,
        isOnline: window.navigator.onLine,
        planPages: [],
        navScrollTop: NAVBUTTONTOPOFFSET,
        curBreakpoint: "xs",
        toolInfo: null,
        mediaToolkitData: [],
        mediaToolkitVerbiageData: {} as IAAPMediaToolkitVerbiageData,
        translations: strings[1033],
        myPlans: [],
        selectedPlan: generateNewPlan(),
        selectedPlanFamily: generateNewFamily(),
        userHasSavedPlanOnAccount: false,
        localAndRemotePlansMatch: true,
        guestPlanLoaded: false,
        guestPlanName: null,
        isViewingGuest: false,
        lcid: ELCID.English,
        currentUser: undefined,
    };

    public componentDidMount(): void {
        window.addEventListener("scroll", this._handleScroll);
        window.addEventListener("resize", this._handleResize);
        window.addEventListener("online", this._handleOnline);
        window.addEventListener("offline", this._handleOffline);

        this._setup();
    }

    public componentWillUnmount(): void {
        window.removeEventListener("scroll", this._handleScroll);
        window.removeEventListener("resize", this._handleResize);
        window.removeEventListener("online", this._handleOnline);
        window.removeEventListener("offline", this._handleOffline);
    }

    private _saveTimer: any = null;

    private _setup = async (): Promise<void> => {
        const curQueryStrings = parseQueryString();

        this.setState({
            loading: true,
        });

        let toolInfo: AAPMediaToolkitPageData | null = null;
        const existingPlanData: string | null = localStorage.getItem(LOCALSTORAGEKEY);
        let localstoragePlanData: IAAPServerPlan | null = null;
        let tempSelectedPlan: IAAPServerPlan = generateNewPlan();
        let tempAllPlans: IAAPServerPlan[] = [tempSelectedPlan];
        let userHasSavedPlanOnAccount: boolean = false;
        let localAndRemotePlansMatch: boolean = true;

        try {
            if (existingPlanData) {
                tempSelectedPlan = JSON.parse(existingPlanData);
                tempAllPlans = [tempSelectedPlan];
                localstoragePlanData = cloneDeep(tempSelectedPlan);
            } else {
                throw new Error("No user plan data exists, starting new");
            }
        } catch {
            // brand new
            this._addMemberToFamily(tempSelectedPlan.family);
        }

        this._getToolInfo()
            .then((responseToolInfo) => {
                toolInfo = responseToolInfo;
                localStorage.setItem(TOOLKITLOCALSTORAGEKEY, JSON.stringify(responseToolInfo));
            })
            .catch((er) => {
                console.log(er);
                // probably an offline issue, use existing localstorage data
                const existingToolData: string | null = localStorage.getItem(TOOLKITLOCALSTORAGEKEY);
                if (existingToolData) {
                    console.log("falling back to localstorage for tool data");
                    toolInfo = JSON.parse(existingToolData);
                }
            })
            .finally(async () => {
                if (!toolInfo) {
                    // total failure on tool data fetch
                    return;
                }
                let guestPlan = null;
                let guestPlanName = null;
                const existingGuestPlanId = localStorage.getItem(LOCALSTORAGEKEY_GUESTID);
                const targetGuestPlanId = curQueryStrings.planid || existingGuestPlanId;
                const lcid: ELCID = parseInt(toolInfo.LCID);
                const mediaToolkitData = this._fixToolkitData(JSON.parse(toolInfo.ScreenDataJSON.toString()));
                const isLoggedIn = toolInfo.LoggedIn.toString() === "true";
                const translations = JSON.parse(toolInfo.PageTranslationJSON.toString());
                const currentUser: AAPMediaToolkitCurrentUserInfo | undefined = toolInfo.CurrentUser
                    ? JSON.parse(toolInfo.CurrentUser.toString())
                    : undefined;

                const mediaToolkitVerbiageData = this._fixVerbiageData(
                    JSON.parse(toolInfo.PageVerbiageJSON.toString())
                );

                const planPages = this._getPages(mediaToolkitData, tempSelectedPlan.family, translations);
                let isViewingGuest = false;
                let guestPlanLoaded = false;

                if (targetGuestPlanId) {
                    if (this.state.isOnline) {
                        // get updated guest plan and set it as local storage
                        guestPlan = await this._getGuestPlan(targetGuestPlanId);
                        localStorage.setItem(LOCALSTORAGEKEY_GUEST, JSON.stringify(guestPlan));
                        localStorage.setItem(LOCALSTORAGEKEY_GUESTID, targetGuestPlanId);
                    } else {
                        guestPlan = JSON.parse(localStorage.getItem(LOCALSTORAGEKEY_GUEST) || "{}");
                    }
                    guestPlanLoaded = true;
                    guestPlanName = guestPlan.family?.name;
                }

                if (curQueryStrings.planid && guestPlan) {
                    // loading in another plan, set everything to read only and load that
                    // ONLY if we are on initial load.  assume anytime we're here its the user breaking out
                    // of the guest to view their own
                    if (!this.state.initiallyLoaded) {
                        isViewingGuest = true;
                        tempSelectedPlan = guestPlan;
                        tempAllPlans = [tempSelectedPlan];
                    }
                } else {
                    // check if user has a plan on thier account
                    if (isLoggedIn && this.state.isOnline) {
                        const myPlans = await this._getMyPlans();

                        if (myPlans.length > 0) {
                            // user has saved plans on their account
                            userHasSavedPlanOnAccount = true;
                            tempAllPlans = myPlans;
                            tempSelectedPlan = tempAllPlans[0];

                            if (localstoragePlanData) {
                                localAndRemotePlansMatch = this._checkIfPlansMatch(
                                    tempSelectedPlan,
                                    localstoragePlanData
                                );
                            }

                            if (myPlans.length > 1) {
                                // delete any other plans that exist
                                // this is a secret background process that shouldn't interrupt the user
                                for (let i = 1; i < myPlans.length; i++) {
                                    const targPlan = myPlans[i];
                                    if (targPlan.id) {
                                        try {
                                            await AAPService.DeletePlan(targPlan.id);
                                        } catch {
                                            // do nothing
                                        }
                                    }
                                }
                            }
                        }
                    } else {
                        //using plans from localstorage
                        // console.log("using plan data from localstorage", existingPlanData);
                    }
                }

                // set default family name to logged in users' last name if applicable
                if (isLoggedIn && currentUser && tempSelectedPlan.family.name === "") {
                    tempSelectedPlan.family.name = currentUser.LastName;
                }

                this._fixLoginHeader(lcid, isLoggedIn, currentUser?.FirstName || "");

                this.setState(
                    {
                        loading: false,
                        initiallyLoaded: true,
                        userHasSavedPlanOnAccount,
                        isLoggedIn,
                        toolInfo,
                        planPages,
                        mediaToolkitData,
                        mediaToolkitVerbiageData,
                        curBreakpoint: this._getBreakpoint(),
                        myPlans: tempAllPlans,
                        selectedPlan: tempSelectedPlan,
                        selectedPlanFamily: tempSelectedPlan.family,
                        localAndRemotePlansMatch,
                        isViewingGuest,
                        lcid,
                        currentUser,
                        guestPlanLoaded,
                        guestPlanName,
                        translations
                    },
                    () => {
                        if (isLoggedIn && !userHasSavedPlanOnAccount) {
                            this._updatePlan();
                        }
                    }
                );
            });
    };

    private _switchToUserPlan = (): void => {
        this._setup();
    };

    private _switchToGuestPlan = (): void => {
        const guestPlan = JSON.parse(localStorage.getItem(LOCALSTORAGEKEY_GUEST) || "{}");
        const tempSelectedPlan = guestPlan;
        const tempAllPlans = [tempSelectedPlan];
        const isViewingGuest = true;

        this.setState({
            myPlans: tempAllPlans,
            selectedPlan: tempSelectedPlan,
            selectedPlanFamily: tempSelectedPlan.family,
            isViewingGuest,
        });
    };

    private _getGuestPlan = async (planid: string): Promise<IAAPServerPlan> => {
        const guestPlan = await AAPService.GetSharedPlan(planid, this.ENCRYPTIONPW);

        return guestPlan;
    };

    private _getToolInfo = async (): Promise<AAPMediaToolkitPageData> => {
        try {
            const toolInfo = await AAPService.GetToolInfo();

            return toolInfo;
        } catch (er) {
            throw new Error(`Error fetching toolInfo: ${er}`);
        }
    };

    private _getMyPlans = async (): Promise<IAAPServerPlan[]> => {
        try {
            const myPlans = await AAPService.GetMyPlans();
            return myPlans;
        } catch (er) {
            return [];
        }
    };

    private _syncPlan = async (planData: IAAPServerPlan): Promise<void> => {
        try {
            await AAPService.SaveMyPlan(planData, this.state.userHasSavedPlanOnAccount);
        } catch {
            console.log("Error saving plan");
        }
    };

    private _handleScroll = () => {
        this.setState({
            navScrollTop: window.scrollY + NAVBUTTONTOPOFFSET,
        });
    };

    private _handleResize = () => {
        const curBreakpoint = this._getBreakpoint();

        if (curBreakpoint !== this.state.curBreakpoint) {
            this.setState({
                curBreakpoint,
            });
        }
    };

    private _handleOnline = () => {
        this.setState({ isOnline: true }, () => {
            this._setup();
        });
    };

    private _handleOffline = () => {
        this.setState({ isOnline: false });
    };

    private _getBreakpoint = (): TBreakpoint => {
        let curBreakpoint: TBreakpoint = "xs";
        for (let key in breakpointWidthMap) {
            if (
                window.innerWidth >= breakpointWidthMap[key as TBreakpoint].min &&
                window.innerWidth <= breakpointWidthMap[key as TBreakpoint].max
            ) {
                curBreakpoint = key as TBreakpoint;
                break;
            }
        }

        return curBreakpoint;
    };

    private _fixVerbiageData = (data: IAAPMediaToolkitVerbiageData): IAAPMediaToolkitVerbiageData => {
        const fixedData: IAAPMediaToolkitVerbiageData = { ...data };

        for (let key in data) {
            fixedData[key as keyof IAAPMediaToolkitVerbiageData] = decodeURIComponent(
                fixedData[key as keyof IAAPMediaToolkitVerbiageData]
            );
        }

        return fixedData;
    };

    private _fixToolkitData = (data: IMediaToolkitDataTopic[]): IMediaToolkitDataTopic[] => {
        data = data.map((topic) => {
            topic.promises = topic.promises.map((promise) => {
                const allPoints: IPromisePoint[] = [];

                for (let key in promise.points) {
                    promise.points[key as keyof typeof promise.points]?.forEach((point) => {
                        const newPoint = cloneDeep(point);
                        const existingPoint = allPoints.filter((allPoint) => allPoint.id === newPoint.id)[0];

                        if (!existingPoint) {
                            newPoint.targetAges = newPoint.targetAges || [];
                            newPoint.targetAges.push(key as EAge);
                            allPoints.push(newPoint);
                        } else {
                            existingPoint.targetAges = existingPoint.targetAges || [];
                            if (existingPoint.targetAges.indexOf(key as EAge) === -1) {
                                existingPoint.targetAges.push(key as EAge);
                            }
                        }
                    });
                }
                allPoints.sort((a, b) => {
                    return parseInt(a.order || "999") - parseInt(b.order || "999");
                });
                promise.allPoints = allPoints;

                return promise;
            });
            return topic;
        });

        return data;
    };

    private _getPages = (data: IMediaToolkitDataTopic[], tempFamily: IFamily, translations: ITranslations): IMediaPlanPage[] => {
        const planPages: IMediaPlanPage[] = [];

        // fix family
        if (!tempFamily.priorities) {
            tempFamily.priorities = [];
        }

        const newFamily = tempFamily.priorities.length === 0;

        // initial pages
        planPages.push({
            title: translations.home,
            identifier: "home",
            icon: "MdHouse",
            navigateTo: "/",
            spoId: -1,
            dontCountAsStep: true,
            showAlways: true,
        });
        planPages.push({
            title: translations.tutorial,
            identifier: "tutorial",
            icon: "MdHelp",
            navigateTo: "/tutorial",
            spoId: -1,
            dontCountAsStep: true,
            hideForGuestView: true,
        });
        planPages.push({
            title: translations.assignFamily,
            identifier: "family",
            icon: "MdPerson",
            navigateTo: "/family",
            spoId: -1,
            hideForGuestView: true,
        });
        planPages.push({
            title: translations.priorities,
            identifier: "priorities",
            icon: "MdFactCheck",
            navigateTo: "/priorities",
            spoId: -1,
            hideForGuestView: true,
        });

        // dynamic pages
        data.sort((a, b) => {
            return parseInt(a.order) - parseInt(b.order);
        }).forEach((topicData) => {
            if (newFamily && topicData.enabledByDefault) {
                tempFamily.priorities.push(topicData.id);
            }

            const pageTitle = decodeURIComponent(topicData.title);
            let icon = topicData.icon || "undefined";

            planPages.push({
                title: pageTitle,
                description: decodeURIComponent(topicData.description),
                identifier: btoa(topicData.title),
                icon,
                navigateTo: `/topic/${topicData.id}`,
                availablePriority: true,
                spoId: topicData.id,
            });
        });

        // final page
        planPages.push({
            title: translations.review,
            identifier: "finish",
            icon: "MdPrint",
            navigateTo: "/finish",
            spoId: -1,
            showAlways: true,
        });

        return planPages;
    };

    private _togglePriority = (pageId: number) => {
        const tempFamily = cloneDeep(this.state.selectedPlanFamily);
        const priorityIndex = tempFamily.priorities.indexOf(pageId);

        if (priorityIndex > -1) {
            tempFamily.priorities.splice(priorityIndex, 1);
        } else {
            tempFamily.priorities.push(pageId);
        }

        this._updateFamily(tempFamily);
    };

    private _updateFamily = (updatedFamily: IFamily): void => {
        if (this.state.isViewingGuest) {
            return;
        }
        const tempUpdatedFamily: IFamily = cloneDeep(updatedFamily);
        const tempCurrentPlan: IAAPServerPlan = cloneDeep(this.state.selectedPlan);

        tempCurrentPlan.family = tempUpdatedFamily;
        tempCurrentPlan.is_public = true;
        tempCurrentPlan.json_encrypted = AAPService.Encrypt(tempUpdatedFamily, this.ENCRYPTIONPW);

        this.setState(
            {
                selectedPlan: tempCurrentPlan,
                selectedPlanFamily: tempUpdatedFamily,
            },
            () => {
                this._updatePlan();
            }
        );
    };

    private _keepLocalPlan = (): void => {
        const existingPlanData: string | null = localStorage.getItem(LOCALSTORAGEKEY);

        let tempSelectedPlan: IAAPServerPlan = generateNewPlan();
        let tempAllPlans: IAAPServerPlan[] = [tempSelectedPlan];

        if (existingPlanData) {
            tempSelectedPlan = JSON.parse(existingPlanData);
            tempAllPlans = [tempSelectedPlan];
        } else {
            throw new Error("No user plan data exists, starting new");
        }

        this.setState(
            {
                loading: false,
                initiallyLoaded: true,
                myPlans: tempAllPlans,
                selectedPlan: tempSelectedPlan,
                selectedPlanFamily: tempSelectedPlan.family,
            },
            () => {
                this._updatePlan();
            }
        );
    };

    private _keepServerPlan = (): void => {
        this._updatePlan();
    };

    private _updatePlan = (): void => {
        if (this.state.isViewingGuest) {
            return;
        }
        this.setState({
            localAndRemotePlansMatch: false,
            saving: true,
        });

        const updatedPlan = this.state.selectedPlan;

        localStorage.setItem(LOCALSTORAGEKEY, JSON.stringify(updatedPlan));

        clearTimeout(this._saveTimer);

        if (this.state.isOnline && this.state.isLoggedIn) {
            this._saveTimer = setTimeout(async () => {
                this.setState({
                    saving: true,
                });

                await this._syncPlan(updatedPlan);
                const localStoragePlan = JSON.parse(localStorage[LOCALSTORAGEKEY]);

                this.setState({
                    saving: false,
                    userHasSavedPlanOnAccount: true,
                    localAndRemotePlansMatch: this._checkIfPlansMatch(updatedPlan, localStoragePlan),
                });
            }, 500);
        } else {
            this.setState({
                saving: false,
            });
        }
    };

    private _updateFamilyName = (name: string): void => {
        const tempFamily = cloneDeep(this.state.selectedPlanFamily);
        tempFamily.name = name;

        this._updateFamily(tempFamily);
    };

    private _generateFamilyMember = (childInfo?: AAPMediaToolkitCurrentUserChild): IFamilyMember => {
        const newFamilyMember = cloneDeep(defaultFamilyMember);
        newFamilyMember.avatar = getRandomAvatar();

        if (childInfo) {
            newFamilyMember.key = childInfo.Key;
            newFamilyMember.name = childInfo.Name;
            newFamilyMember.age = AAPService.NormalizeAge(childInfo.DateOfBirth);
        }

        return newFamilyMember;
    };

    private _addMemberToFamily = (
        tempFamily: IFamily,
        newFamilyMember: IFamilyMember = this._generateFamilyMember()
    ): IFamily => {
        tempFamily.members.push(newFamilyMember);

        return tempFamily;
    };

    private _addMembersFromAAPAccount = (): void => {
        if (!this.state.currentUser) {
            return;
        }

        const tempFamily = cloneDeep(this.state.selectedPlanFamily);

        for (let i = 0; i < this.state.currentUser.Children.length; i++) {
            const accountMember: AAPMediaToolkitCurrentUserChild = this.state.currentUser.Children[i];

            if (!this._isMemberFromAAPAccountInFamily(accountMember)) {
                this._addMemberToFamily(tempFamily, this._generateFamilyMember(accountMember));
            }
        }

        this._updateFamily(tempFamily);
    };

    private _isMemberFromAAPAccountInFamily = (accountMember: AAPMediaToolkitCurrentUserChild): boolean => {
        const currentlyAddedMemberKeys = this.state.selectedPlanFamily.members.map((member) => member.key);

        return currentlyAddedMemberKeys.indexOf(accountMember.Key) > -1;
    };

    private _updateMemberOfFamily = (data: IFamilyMember, idx: number): void => {
        const tempFamily = cloneDeep(this.state.selectedPlanFamily);
        tempFamily.members[idx] = data;

        this._updateFamily(tempFamily);
    };

    private _updateAllMembersOfFamily = (members: IFamilyMember[]): void => {
        const tempFamily = cloneDeep(this.state.selectedPlanFamily);
        tempFamily.members = members;

        this._updateFamily(tempFamily);
    };

    private _deleteMemberOfFamily = (idx: number): void => {
        let tempFamily = cloneDeep(this.state.selectedPlanFamily);
        tempFamily.members.splice(idx, 1);

        this._updateFamily(tempFamily);
    };

    private _addCustomPoint = (promiseId: number, topicId: number) => {
        const tempFamily = cloneDeep(this.state.selectedPlanFamily);

        const uniqueId = new Date().getTime();
        tempFamily.customPoints.push({
            id: uniqueId,
            title: "",
            promiseId,
            topicId,
            order: null,
            description: null,
            isCustom: true,
        });

        this._updateFamily(tempFamily);
    };

    private _updateCustomPoint = (point: ICustomPoint) => {
        const tempFamily = cloneDeep(this.state.selectedPlanFamily);
        const tempCustomPoints = cloneDeep(tempFamily.customPoints);

        tempCustomPoints.forEach((tempPoint, pointIdx) => {
            if (tempPoint.id === point.id) {
                tempPoint.title = point.title;
            }
        });

        tempFamily.customPoints = tempCustomPoints;
        this._updateFamily(tempFamily);
    };

    private _deleteCustomPoint = (pointId: number) => {
        const tempFamily = cloneDeep(this.state.selectedPlanFamily);

        let indexOfPoint = -1;

        tempFamily.customPoints.forEach((point, pointIdx) => {
            if (point.id === pointId) {
                indexOfPoint = pointIdx;
            }
        });

        if (indexOfPoint > -1) {
            tempFamily.customPoints.splice(indexOfPoint, 1);
        }

        this._updateFamily(tempFamily);
    };

    private _checkIfPlansMatch = (planA: IAAPServerPlan, planB: IAAPServerPlan): boolean => {
        return JSON.stringify(planA.family) === JSON.stringify(planB.family);
    };

    private _getPlanPagesForDisplay = (): IMediaPlanPage[] => {
        return this.state.planPages.filter((page) => {
            if (this.state.isViewingGuest) {
                return (
                    (!page.hideForGuestView && this.state.selectedPlanFamily.priorities.indexOf(page.spoId) > -1) ||
                    page.showAlways
                );
            }
            return true;
        });
    };

    private _getCtaMap = (backButton?: JSX.Element, fwdButton?: JSX.Element): JSX.Element => {
        return (
            <div className="bg-aap-cream-500 sticky top-9 sm:top-12 md:top-[44px] lg:top-[56px] z-40 mb-5 print:hidden -mx-5 xl:-mx-10">
                <CtaMap
                    pages={this._getPlanPagesForDisplay()}
                    family={this.state.selectedPlanFamily}
                    curBreakpoint={this.state.curBreakpoint}
                    lcid={this.state.lcid}
                    translations={this.state.translations}
                />
                {backButton || fwdButton ? (
                    <div className="mobileNav py-2 flex justify-between px-5 xl:px-0 lg:hidden">
                        {backButton ? backButton : null}
                        {fwdButton ? fwdButton : null}
                    </div>
                ) : null}
            </div>
        );
    };

    private _fixLoginHeader = (lcid: ELCID, loggedIn: boolean, userFirstname: string): void => {
        let newHeader: JSX.Element = <div></div>;
        const languageStr = lcid === ELCID.English ? "english" : "spanish";
        const pageLibStr = lcid === ELCID.English ? "pages" : "paginas";

        if (loggedIn) {
            newHeader = (
                <div id="FamilyMediaPlanControl_aapTopNavigation_pnlLoggedIn">
                    {lcid === ELCID.English ? `Welcome` : `Bienvenido`},&nbsp;
                    <a
                        id="FamilyMediaPlanControl_aapTopNavigation_lnkUser"
                        className="registered-user"
                        href={`/${languageStr}/${pageLibStr}/my-account.aspx`}
                    >
                        {userFirstname}
                    </a>
                    &nbsp;|&nbsp;
                    <a
                        id="FamilyMediaPlanControl_aapTopNavigation_lnkBtnLogOut"
                        href={`javascript:__doPostBack("FamilyMediaPlanControl$aapTopNavigation$lnkBtnLogOut", "")`}
                    >
                        {lcid === ELCID.English ? `Log Out` : `Terminar`}
                    </a>
                </div>
            );
        } else {
            newHeader = (
                <div id="FamilyMediaPlanControl_aapTopNavigation_pnlLoggedOut">
                    <a
                        id="FamilyMediaPlanControl_aapTopNavigation_lnkLogin"
                        className="registered-user"
                        href={`/${languageStr}/${pageLibStr}/login.aspx?ReturnURL=%2f${languageStr}%2ffmp%2f${pageLibStr}%2fmediaplan.aspx`}
                    >
                        {lcid === ELCID.English ? `Log in` : `Ingresar`}
                    </a>
                    &nbsp;|&nbsp;
                    <a
                        id="FamilyMediaPlanControl_aapTopNavigation_lnkRegister"
                        className="registered-user"
                        href={`/${languageStr}/${pageLibStr}/registerinformation.aspx`}
                    >
                        {lcid === ELCID.English ? `Register` : `Inscribirse`}
                    </a>
                </div>
            );
        }

        const existingHeader = document.querySelectorAll(".greeting-registration");

        for (let i = 0; i < existingHeader.length; i++) {
            const targHeader = existingHeader[i];
            ReactDOM.render(newHeader, targHeader);
            targHeader.classList.add("fromApp");
        }
    };

    private _getFirstValidPage = (): IMediaPlanPage => {
        return this.state.planPages.filter((page) => {
            if (this.state.isViewingGuest) {
                return !page.hideForGuestView && this.state.selectedPlanFamily.priorities.indexOf(page.spoId) > -1;
            }

            return true;
        })[0];
    };

    public render(): JSX.Element {
        if (!this.state.initiallyLoaded) {
            return (
                <div className="media-toolkit-wrap container mx-auto py-10 lg:py-16 lg:px-20 relative print:static top-16 lg:top-16 print:w-full print:mx-0 print:max-w-full">
                    <div className="card shadow-xl shadow-black rounded-sm bg-aap-cream-500 relative z-20 print:shadow-none">
                        <div className="card-header bg-aap-creamy-gray-500 text-aap-dk-red-500 px-5 xl:px-10 py-5 pt-10">
                            {this.state.translations.loading}
                        </div>
                    </div>
                </div>
            );
        }

        const commonNavBtnProps: Pick<INavButtonProps, "family" | "pages" | "lcid"> = {
            family: this.state.selectedPlanFamily,
            pages: this._getPlanPagesForDisplay(),
            lcid: this.state.lcid,
        };

        const mobileBackButton: JSX.Element = (
            <NavButton
                {...commonNavBtnProps}
                variant={`mobile`}
                type={`Back`}
                arrow={<MdArrowBack size={24} className={`inline`} />}
                translations={this.state.translations}
            />
        );
        const mobileContinueButton: JSX.Element = (
            <NavButton
                {...commonNavBtnProps}
                variant={`mobile`}
                type={`Forward`}
                arrow={<MdArrowForward size={24} className={`inline`} />}
                translations={this.state.translations}
            />
        );

        const commonPageProps: Pick<
            IMediaToolkitNavPage,
            "ctaMap" | "family" | "topics" | "backButton" | "continueButton" | "pages" | "lcid"
        > = {
            ctaMap: this._getCtaMap(mobileBackButton, mobileContinueButton),
            family: this.state.selectedPlanFamily,
            topics: this.state.mediaToolkitData,
            backButton: mobileBackButton,
            continueButton: mobileContinueButton,
            pages: this._getPlanPagesForDisplay(),
            lcid: this.state.lcid,
        };

        return (
            <HashRouter>
                <div className="media-toolkit-wrap container mx-auto py-10 lg:py-16 lg:px-20 relative print:static top-16 lg:top-16 print:w-full print:mx-0 print:max-w-full">
                    <div className="print:hidden">
                        <NavButton
                            {...commonNavBtnProps}
                            variant={`floating`}
                            topPosition={this.state.navScrollTop}
                            type={`Back`}
                            arrow={<MdArrowBack size={28} className="mx-auto" />}
                            className={`hidden lg:block`}
                            translations={this.state.translations}
                        />
                    </div>

                    <div className="card shadow-xl shadow-black rounded-sm bg-aap-cream-500 relative z-20 print:shadow-none">
                        <div className="status absolute top-2 right-2 text-xs print:hidden">
                            {this.state.loading ? (
                                <div className={`inline-block px-2 py-1 ml-3 rounded-md text-aap-orange-500`}>
                                    {this.state.translations.comingBackOnline}
                                </div>
                            ) : this.state.isLoggedIn &&
                              !this.state.isViewingGuest &&
                              !this.state.localAndRemotePlansMatch ? (
                                <div
                                    className={`inline-block text-white px-2 py-1 ml-3 rounded-md ${
                                        this.state.localAndRemotePlansMatch ? `bg-aap-green-700` : `bg-aap-orange-500`
                                    }`}
                                >
                                    {this.state.translations.desync}
                                    {this.state.isOnline && !this.state.saving ? (
                                        <>
                                            <button
                                                type="button"
                                                className="text-xs p-0.5 rounded-sm bg-white text-aap-orange-500 ml-2"
                                                onClick={() => this._keepLocalPlan()}
                                                id={`btn_keepLocalPlan_${ELCID[this.state.lcid]}`}
                                            >
                                                {this.state.translations.keepLocal}
                                            </button>
                                            <button
                                                type="button"
                                                className="text-xs p-0.5 rounded-sm bg-white text-aap-orange-500 ml-2"
                                                onClick={() => this._keepServerPlan()}
                                                id={`btn_keepServerPlan_${ELCID[this.state.lcid]}`}
                                            >
                                                {this.state.translations.keepServer}
                                            </button>
                                        </>
                                    ) : null}
                                </div>
                            ) : null}
                            {!this.state.isViewingGuest ? (
                                <div
                                    className={`inline-block text-white px-2 py-1 ml-3 rounded-md ${
                                        !this.state.saving ? `text-aap-green-700` : `text-aap-orange-500`
                                    }`}
                                >
                                    {!this.state.saving
                                        ? this.state.translations.saved
                                        : this.state.translations.saving}
                                </div>
                            ) : null}
                            {this.state.guestPlanLoaded ? (
                                <DropdownOptions
                                    lcid={this.state.lcid}
                                    options={[
                                        {
                                            title: `View My Media Plan`,
                                            selectedTitle: `Viewing My Media Plan`,
                                            func: this._switchToUserPlan,
                                            selected: !this.state.isViewingGuest,
                                            type: "self",
                                        },
                                        {
                                            title: `View ${this.state.guestPlanName} Family Plan`,
                                            selectedTitle: `Viewing ${this.state.guestPlanName} Family Plan`,
                                            func: this._switchToGuestPlan,
                                            selected: this.state.isViewingGuest,
                                            type: "guest",
                                        },
                                    ]}
                                />
                            ) : null}
                        </div>
                        <Routes>
                            <Route
                                path="/"
                                element={
                                    <LandingPage
                                        {...commonPageProps}
                                        title={this.state.mediaToolkitVerbiageData["homepage-title"]}
                                        description={this.state.mediaToolkitVerbiageData["homepage-intro"]}
                                        footer={this.state.mediaToolkitVerbiageData["homepage-footer"]}
                                        createTitle={this.state.mediaToolkitVerbiageData["wizard-title"]}
                                        createDescription={
                                            this.state.mediaToolkitVerbiageData["wizard-shortDescription"]
                                        }
                                        tutorialTitle={this.state.mediaToolkitVerbiageData["tutorial-title"]}
                                        tutorialDescription={
                                            this.state.mediaToolkitVerbiageData["tutorial-shortDescription"]
                                        }
                                        readonly={this.state.isViewingGuest}
                                        guestPlanFirstPage={this._getFirstValidPage().navigateTo}
                                    />
                                }
                            />
                            <Route
                                path="/tutorial"
                                element={
                                    <TutorialPage
                                        {...commonPageProps}
                                        title={this.state.mediaToolkitVerbiageData["tutorial-title"]}
                                        description={this.state.mediaToolkitVerbiageData["tutorial-intro"]}
                                        footer={this.state.mediaToolkitVerbiageData["tutorial-footer"]}
                                        videoUrl={this.state.mediaToolkitVerbiageData["tutorial-videoUrl"]}
                                        readonly={this.state.isViewingGuest}
                                    />
                                }
                            />
                            <Route
                                path="/family"
                                element={
                                    <FamilyConfigPage
                                        {...commonPageProps}
                                        title={this.state.mediaToolkitVerbiageData["wizard-title"]}
                                        description={this.state.mediaToolkitVerbiageData["wizard-intro"]}
                                        footer={this.state.mediaToolkitVerbiageData["wizard-footer"]}
                                        currentUser={this.state.currentUser}
                                        addMember={() => {
                                            const tempFamily = cloneDeep(this.state.selectedPlanFamily);
                                            this._addMemberToFamily(tempFamily);

                                            this._updateFamily(tempFamily);
                                        }}
                                        updateMember={this._updateMemberOfFamily}
                                        deleteMember={this._deleteMemberOfFamily}
                                        updateFamilyName={this._updateFamilyName}
                                        addMembersFromAAPAccount={this._addMembersFromAAPAccount}
                                        readonly={this.state.isViewingGuest}
                                        isAccountMemberAdded={this._isMemberFromAAPAccountInFamily}
                                        translations={this.state.translations}
                                    />
                                }
                            />
                            <Route
                                path="/priorities"
                                element={
                                    <PrioritiesPage
                                        {...commonPageProps}
                                        title={this.state.mediaToolkitVerbiageData["calculator-title"]}
                                        description={this.state.mediaToolkitVerbiageData["calculator-intro"]}
                                        footer={this.state.mediaToolkitVerbiageData["calculator-footer"]}
                                        togglePriority={this._togglePriority}
                                        updateFamily={this._updateFamily}
                                        backButton={mobileBackButton}
                                        continueButton={mobileContinueButton}
                                        readonly={this.state.isViewingGuest}
                                        translations={this.state.translations}
                                    />
                                }
                            />
                            <Route
                                path="/topic/:topicid"
                                element={
                                    <TopicPage
                                        {...commonPageProps}
                                        title={"This should get overriden by the point"}
                                        description={"This should also get overriden by the point"}
                                        updateAllMembers={this._updateAllMembersOfFamily}
                                        addCustomPoint={this._addCustomPoint}
                                        updateCustomPoint={this._updateCustomPoint}
                                        deleteCustomPoint={this._deleteCustomPoint}
                                        togglePriority={this._togglePriority}
                                        readonly={this.state.isViewingGuest}
                                        talktoTitle={this.state.mediaToolkitVerbiageData["reason-talkingpointsTitle"]}
                                        scienceTitle={this.state.mediaToolkitVerbiageData["reason-scienceTitle"]}
                                        translations={this.state.translations}
                                    />
                                }
                            />
                            <Route
                                path="/finish"
                                element={
                                    <FinishPage
                                        {...commonPageProps}
                                        title={this.state.translations.userMediaPlanTitle.replace(
                                            "{{FAMILYNAME}}",
                                            this.state.selectedPlanFamily.name
                                        )}
                                        description={this.state.mediaToolkitVerbiageData["planview-footer"]}
                                        allowEmail={
                                            this.state.isOnline &&
                                            this.state.isLoggedIn &&
                                            this.state.localAndRemotePlansMatch
                                        }
                                        isOnline={this.state.isOnline}
                                        planid={this.state.selectedPlan.plan_guid}
                                        readonly={this.state.isViewingGuest}
                                        globalFooter={this.state.mediaToolkitVerbiageData["global-footer"]}
                                        talktoTitle={this.state.mediaToolkitVerbiageData["reason-talkingpointsTitle"]}
                                        scienceTitle={this.state.mediaToolkitVerbiageData["reason-scienceTitle"]}
                                        translations={this.state.translations}
                                    />
                                }
                            />
                        </Routes>

                        {this.state.mediaToolkitVerbiageData["global-footer"] ? (
                            <div
                                className="global-footer px-5 xl:px-10 pb-5 xl:pb-10 clearfix print:hidden"
                                dangerouslySetInnerHTML={{
                                    __html: this.state.mediaToolkitVerbiageData["global-footer"],
                                }}
                            ></div>
                        ) : null}
                    </div>

                    <div className="print:hidden">
                        <NavButton
                            {...commonNavBtnProps}
                            variant={`floating`}
                            topPosition={this.state.navScrollTop}
                            type={`Forward`}
                            arrow={<MdArrowForward size={28} className="mx-auto" />}
                            className={`hidden lg:block print:hidden`}
                            translations={this.state.translations}
                        />
                    </div>
                </div>
            </HashRouter>
        );
    }
}
